import { Contact, Paragraph } from 'components';
import React, { useContext, useEffect, useMemo } from 'react';
import { BrokerageInfo } from 'components/UniversalSearch/components';
import { User } from '@bb/users';
import type { JsonObject } from '@/app-package';
import { AuthContext, ContextualChatContext } from 'contexts';
import Section from '../Section';
import DetailRow from '../DetailRow';
import { ShowingDetailsAppointmentType } from '../../types';

type AgentSectionProps = {
  reps?: JsonObject<User>[];
  appointmentCoop: ShowingDetailsAppointmentType['coop'];
  org?: { name: string; logoUrl?: string };
  dashboardId?: string;
  listingId: string;
  isExternal?: boolean;
};

const AgentSection: React.FC<AgentSectionProps> = ({
  reps,
  appointmentCoop,
  org,
  dashboardId,
  listingId,
  isExternal,
}) => {
  const { auth } = useContext(AuthContext);

  const { setButtonSettings, buttonSettings } = useContext(
    ContextualChatContext,
  );

  const isCurrentUserTheShowingAgent = appointmentCoop?.id === auth?.id;

  const isCurrentUserAListingAgent = useMemo(
    () => reps.some(rep => rep.id === auth?.id),
    [reps, auth?.id],
  );
  const showShowingAgentInfo =
    (isCurrentUserAListingAgent || auth?.isAdmin) &&
    !isCurrentUserTheShowingAgent;

  const enableShowingAgentListingChat =
    isCurrentUserAListingAgent && !isCurrentUserTheShowingAgent;

  useEffect(() => {
    // external listings do not have chat
    if (isExternal || !appointmentCoop?.id) return;

    const chatAgents = [
      ...(reps?.length
        ? reps.reduce<string[]>((acc, rep) => {
            // Auth Id is included in the chat users array in useStartChat as they are the person to initiate the chat
            if (rep.id !== auth?.id) acc.push(rep.id);
            return acc;
          }, [])
        : []),
      // We only add the showing agent id if they are not the current user as they are included in the chat users array in useStartChat
      ...(appointmentCoop?.id && !isCurrentUserTheShowingAgent
        ? [appointmentCoop.id]
        : []),
    ];
    // There are cases where the listing agent is also the showing agent or the showing is for a non-agent. We just want to keep the chat button to open the modal
    if (
      (isCurrentUserAListingAgent && isCurrentUserTheShowingAgent) ||
      appointmentCoop?.isNotAgent ||
      !chatAgents.length
    )
      return;

    if (enableShowingAgentListingChat) {
      // Add the reps to the chat button context to be able to create a listing-centric conversation
      setButtonSettings({
        ...buttonSettings,
        repIds: chatAgents,
        listingId,
        // We need to use the showing agent to create the channel ID for a 1:1 relationship
        ...(appointmentCoop?.id && {
          userIdToCreateChannelId: appointmentCoop.id,
        }),
      });
    }
    if (isCurrentUserTheShowingAgent) {
      // By default we start a chat with the listing agents only
      setButtonSettings({
        ...buttonSettings,
        repIds: chatAgents.filter(id => id !== appointmentCoop?.id),
        listingId,
      });
    }
  }, [
    isExternal,
    listingId,
    auth?.id,
    appointmentCoop?.id,
    enableShowingAgentListingChat,
    reps,
  ]);

  if ((!reps || reps.length === 0) && !org) return null;

  return (
    // The agents displayed vary depending on whether showing or listing agent is viewing this page
    <Section title="Agent">
      {showShowingAgentInfo ? (
        <DetailRow icon="userRectangle" iconPaddingTop={10}>
          <Contact
            key={appointmentCoop.id}
            contact={{
              firstName: appointmentCoop.firstName,
              lastName: appointmentCoop.lastName,
              id: appointmentCoop.id,
              avatarFilestackId: appointmentCoop.avatarFilestackId,
            }}
            disableClick={!!dashboardId}
            viewNameOnToolTip={!!dashboardId}
            avatarSize={32}
          />
        </DetailRow>
      ) : (
        // We show the listing agents by default
        !!reps?.length && (
          <DetailRow icon="userRectangle" iconPaddingTop={10}>
            {reps.map(rep => (
              <Contact
                key={rep.id}
                contact={rep}
                disableClick={!!dashboardId}
                viewNameOnToolTip={!!dashboardId}
                avatarSize={32}
              />
            ))}
          </DetailRow>
        )
      )}
      {showShowingAgentInfo
        ? appointmentCoop?.org && (
            <DetailRow icon="building" iconPaddingTop={6}>
              <Paragraph ellipsis={{ rows: 4 }}>
                {appointmentCoop?.org.name}
              </Paragraph>
            </DetailRow>
          )
        : org && (
            <DetailRow icon="building" iconPaddingTop={6}>
              <BrokerageInfo orgName={org.name} orgLogoUrl={org.logoUrl} />
            </DetailRow>
          )}
    </Section>
  );
};

export default AgentSection;
